import React, { createContext, useContext, useState, useEffect } from 'react';
import { firebaseService } from '../services/firebase';
import { apiService } from '../services/api';
import { storageService } from '../utils/storage';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [backendUser, setBackendUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [permissions, setPermissions] = useState({
    canAccessIntelligentSearch: false
  });
  

  // Fetch user data from backend
  const fetchUserData = async () => {
    try {
      const { data: authStatus } = await apiService.getAuthStatus();
      if (authStatus && authStatus.is_authenticated) {
        setBackendUser(authStatus.user);
        
        // Update permissions based on user data
        setPermissions({
          canAccessIntelligentSearch: authStatus.user?.can_access_intelligent_search || false
        });
        
        return authStatus.user;
      }
      return null;
    } catch (error) {
      console.error('Error getting backend user data:', error);
      return null;
    }
  };

  // Refresh user status in backend
  const refreshUserStatus = async () => {
    try {
      const { data, error } = await apiService.refreshUserStatus();
      if (data) {
        // Immediately update backendUser with the refreshed data
        setBackendUser(data.user);
        
        // Update permissions
        setPermissions({
          canAccessIntelligentSearch: data.user?.can_access_intelligent_search || false
        });
        
        // Also fetch fresh auth status to ensure we have all the latest data
        await fetchUserData();
        return { success: true };
      }
      return { success: false, error };
    } catch (error) {
      console.error('Failed to refresh user status:', error);
      return { success: false, error: error.message };
    }
  };

  // Initialize auth state
  useEffect(() => {
    let unsubscribe;

    const initializeAuth = async () => {
      try {
        // Check for stored user
        const storedUser = storageService.getUser();
        if (storedUser) {
          setUser(storedUser);
        }

        // Set up Firebase auth state listener
        unsubscribe = firebaseService.onAuthStateChanged(async (firebaseUser) => {
          setLoading(true);
          
          if (firebaseUser) {
            // User is signed in
            const userData = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName,
              photoURL: firebaseUser.photoURL,
              emailVerified: firebaseUser.emailVerified,
              isAnonymous: firebaseUser.isAnonymous,
              userType: firebaseService.getUserType(firebaseUser)
            };

            setUser(userData);
            storageService.setUser(userData);

            // Get backend user data
            await fetchUserData();
          } else {
            // User is signed out
            setUser(null);
            setBackendUser(null);
            setPermissions({
              canAccessIntelligentSearch: false
            });
            storageService.clearAuthData();
          }
          
          setLoading(false);
        });
      } catch (error) {
        console.error('Error initializing auth:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  // Sign in with email and password
  const signInWithEmail = async (email, password) => {
    setLoading(true);
    try {
      const { user: firebaseUser, error } = await firebaseService.signInWithEmail(email, password);
      
      if (error) {
        toast.error(error);
        setLoading(false);
        return { success: false, error };
      }

      if (!firebaseUser.emailVerified) {
        toast.error('Please verify your email before signing in.');
        await firebaseService.signOut();
        setLoading(false);
        return { success: false, error: 'Email not verified' };
      }

      toast.success('Signed in successfully!');
      setLoading(false);
      return { success: true, user: firebaseUser };
    } catch (error) {
      console.error('Error signing in:', error);
      toast.error('Failed to sign in');
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  // Create account with email and password
  const createUserWithEmail = async (email, password, displayName) => {
    setLoading(true);
    try {
      const { user: firebaseUser, error } = await firebaseService.createUserWithEmail(email, password, displayName);
      
      if (error) {
        toast.error(error);
        setLoading(false);
        return { success: false, error };
      }

      setEmailVerificationSent(true);
      toast.success('Account created! Please check your email for verification.');
      setLoading(false);
      return { success: true, user: firebaseUser };
    } catch (error) {
      console.error('Error creating account:', error);
      toast.error('Failed to create account');
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const { user: firebaseUser, error } = await firebaseService.signInWithGoogle();
      
      if (error) {
        toast.error(error);
        setLoading(false);
        return { success: false, error };
      }

      toast.success('Signed in with Google successfully!');
      setLoading(false);
      return { success: true, user: firebaseUser };
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast.error('Failed to sign in with Google');
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  // Sign in anonymously
  const signInAnonymously = async () => {
    setLoading(true);
    try {
      const { user: firebaseUser, error } = await firebaseService.signInAnonymously();
      
      if (error) {
        toast.error(error);
        setLoading(false);
        return { success: false, error };
      }

      toast.success('Signed in as guest!');
      setLoading(false);
      return { success: true, user: firebaseUser };
    } catch (error) {
      console.error('Error signing in anonymously:', error);
      toast.error('Failed to sign in as guest');
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  // Link anonymous account with email/password
  const linkAnonymousWithEmail = async (email, password, displayName) => {
  setLoading(true);
  try {
    const { user: firebaseUser, error } = await firebaseService.linkAnonymousWithEmail(email, password, displayName);
    
    if (error) {
      toast.error(error);
      setLoading(false);
      return { success: false, error };
    }

    // Reload Firebase user to get latest state
    await firebaseUser.reload();
    const refreshedUser = firebaseService.getCurrentUser();

    // Update local user state immediately
    const userData = {
      uid: refreshedUser.uid,
      email: refreshedUser.email,
      displayName: refreshedUser.displayName,
      photoURL: refreshedUser.photoURL,
      emailVerified: refreshedUser.emailVerified,
      isAnonymous: refreshedUser.isAnonymous,
      userType: refreshedUser.isAnonymous ? 'guest' : 'standard'
    };
    setUser(userData);
    storageService.setUser(userData);

    // Refresh backend status - WAIT for it to complete
    const refreshResult = await refreshUserStatus();
    if (refreshResult.success) {
      // Force fetch latest data
      await fetchUserData();
    }

    setEmailVerificationSent(true);
    toast.success('Account linked! Please verify your email.');
    setLoading(false);
    return { success: true, user: refreshedUser };
  } catch (error) {
    console.error('Error linking account with email:', error);
    toast.error('Failed to link account');
    setLoading(false);
    return { success: false, error: error.message };
  }
};

  // Link anonymous account with Google
  const linkAnonymousWithGoogle = async () => {
  setLoading(true);
  try {
    const { user: firebaseUser, error } = await firebaseService.linkAnonymousWithGoogle();
    
    if (error) {
      toast.error(error);
      setLoading(false);
      return { success: false, error };
    }

    // Reload Firebase user to get latest state
    await firebaseUser.reload();
    const refreshedUser = firebaseService.getCurrentUser();

    // Update local user state immediately
    const userData = {
      uid: refreshedUser.uid,
      email: refreshedUser.email,
      displayName: refreshedUser.displayName,
      photoURL: refreshedUser.photoURL,
      emailVerified: refreshedUser.emailVerified,
      isAnonymous: refreshedUser.isAnonymous,
      userType: refreshedUser.emailVerified ? 'standard' : 'guest'
    };
    setUser(userData);
    storageService.setUser(userData);

    // Refresh backend status - WAIT for it to complete
    const refreshResult = await refreshUserStatus();
    if (refreshResult.success) {
      // Force fetch latest data
      await fetchUserData();
    }

    toast.success('Account linked with Google successfully!');
    setLoading(false);
    return { success: true, user: refreshedUser };
  } catch (error) {
    console.error('Error linking account with Google:', error);
    toast.error('Failed to link account with Google');
    setLoading(false);
    return { success: false, error: error.message };
  }
};

  // Resend email verification
  const resendEmailVerification = async () => {
    try {
      const { success, error } = await firebaseService.resendEmailVerification();
      
      if (error) {
        toast.error(error);
        return { success: false, error };
      }

      toast.success('Verification email sent!');
      setEmailVerificationSent(true);
      return { success: true };
    } catch (error) {
      console.error('Error resending verification:', error);
      toast.error('Failed to resend verification email');
      return { success: false, error: error.message };
    }
  };

  // Sign out
  const signOut = async () => {
    setLoading(true);
    try {
      const { success, error } = await firebaseService.signOut();
      
      if (error) {
        toast.error(error);
        setLoading(false);
        return { success: false, error };
      }

      toast.success('Signed out successfully!');
      setLoading(false);
      return { success: true };
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
      setLoading(false);
      return { success: false, error: error.message };
    }
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const { data, error } = await apiService.updateUserProfile(profileData);
      
      if (error) {
        toast.error(error.message || 'Failed to update profile');
        return { success: false, error };
      }

      setBackendUser(data);
      
      // Update permissions
      setPermissions({
        canAccessIntelligentSearch: data?.can_access_intelligent_search || false
      });
      
      toast.success('Profile updated successfully!');
      return { success: true, user: data };
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
      return { success: false, error: error.message };
    }
  };

  // Get user statistics
  const getUserStats = async () => {
    try {
      const { data, error } = await apiService.getUserStats();
      
      if (error) {
        console.error('Error getting user stats:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error getting user stats:', error);
      return { success: false, error: error.message };
    }
  };

  // Helper functions
  const isAuthenticated = () => !!user;
  const isAnonymous = () => user?.isAnonymous || false;
  const isEmailVerified = () => user?.emailVerified || false;
  const isAdmin = () => backendUser?.user_type === 'admin';
  const getUserType = () => backendUser?.user_type || user?.userType || 'guest';
  
  const getRemainingRequests = () => {
    if (backendUser?.remaining_requests !== undefined) {
      return backendUser.remaining_requests;
    }
    // Fallback calculation
    const limit = getRequestLimit();
    const used = backendUser?.api_requests_count || 0;
    return Math.max(0, limit - used);
  };

  const getRequestLimit = () => {
    if (backendUser?.request_limit !== undefined) {
      return backendUser.request_limit;
    }
    // Fallback based on user type
    const userType = backendUser?.user_type || user?.userType || 'guest';
    return userType === 'standard' ? 10 : 5;
  };

  const getAreaLimit = () => {
    return getRequestLimit(); 
  };

  const value = {
    // State
    user,
    backendUser,
    loading,
    emailVerificationSent,
    permissions,
    
    // Auth methods
    signInWithEmail,
    createUserWithEmail,
    signInWithGoogle,
    signInAnonymously,
    linkAnonymousWithEmail,
    linkAnonymousWithGoogle,
    resendEmailVerification,
    signOut,
    
    // Profile methods
    updateProfile,
    getUserStats,
    fetchUserData,
    refreshUserStatus,
    
    // Helper methods
    isAuthenticated,
    isAnonymous,
    isEmailVerified,
    isAdmin,
    getUserType,
    getRemainingRequests,
    getRequestLimit,
    getAreaLimit,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;